import { useLocation, matchPath } from 'react-router-dom';

import routes from 'routes/routes';

import { Route } from '../routes/routes';

type Compiled = { key: string; route: Route; pattern: { path: string; end: boolean } };

const COMPILED_ROUTES: Compiled[] = Object.keys(routes)
  .map((k) => {
    const r = (routes as unknown as Record<string, unknown>)[k] as Route | undefined;
    if (!r || !r.path) return null;
    return { key: k, route: r, pattern: { path: r.path, end: true } } as Compiled;
  })
  .filter(Boolean) as Compiled[];

const LRU_CAPACITY = 200;
const routeCache = new Map<string, Route>();

function getCachedRoute(pathname: string): Route | undefined {
  const r = routeCache.get(pathname);
  if (!r) return undefined;
  routeCache.delete(pathname);
  routeCache.set(pathname, r);
  return r;
}

function setCachedRoute(pathname: string, route: Route) {
  if (routeCache.has(pathname)) routeCache.delete(pathname);
  routeCache.set(pathname, route);
  if (routeCache.size > LRU_CAPACITY) {
    const iterator: IterableIterator<string> = routeCache.keys();
    const next = iterator.next();
    if (!next.done && typeof next.value === 'string') {
      routeCache.delete(next.value);
    }
  }
}

const useCurrentRoute = (): Route => {
  const location = useLocation();

  const cached = getCachedRoute(location.pathname);
  if (cached) return cached;

  let currentRoute = (routes as Record<string, Route>)[location.pathname];

  if (!currentRoute) {
    for (let i = 0; i < COMPILED_ROUTES.length; i += 1) {
      const candidate = COMPILED_ROUTES[i];
      const matched = matchPath(candidate.pattern, location.pathname);
      if (matched) {
        currentRoute = candidate.route;
        break;
      }
    }
  }

  if (!currentRoute) {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstLevel = `/${pathSegments[0] || ''}`;
    if ((routes as Record<string, Route>)[firstLevel]) {
      currentRoute = (routes as Record<string, Route>)[firstLevel];
    }
  }

  if (!currentRoute) currentRoute = (routes as Record<string, Route>).error;

  setCachedRoute(location.pathname, currentRoute);

  return currentRoute;
};

export default useCurrentRoute;
